import React, { useState, useCallback, useEffect } from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { useAppStore } from "@/core/store/useAppStore";
import { AuthenticatedImage } from "@/components/ui/AuthenticatedImage";
import { fileCacheService } from "@/core/cache/fileCacheService";
import { MediaStatus } from "@/core/types/mediaTypes";

interface UnifiedAuthenticatedImageProps {
  mediaItem: MediaItem;
  className?: string;
  aspectRatio?: "4:5" | "1:1" | "16:9" | "9:16" | "auto";
  showLabel?: boolean;
  onClick?: () => void;
  alt?: string;
  hoverEffect?: boolean;
  isTaken?: boolean;
  useThumbnail?: boolean;
  loading?: "lazy" | "eager";
  fallbackComponent?: React.ReactNode;
  isReelThumbnail?: boolean; // New prop to indicate if this is a reel thumbnail
}

type LoadingState = 'loading' | 'downloading' | 'retrying' | 'loaded' | 'cached' | 'error' | 'pending_download' | 'generating';

/**
 * Unified component for displaying authenticated images across the app
 * Handles caching, authentication, and all loading states consistently
 */
const UnifiedAuthenticatedImageComponent: React.FC<UnifiedAuthenticatedImageProps> = ({
  mediaItem,
  className = "",
  aspectRatio = "auto",
  showLabel = false,
  onClick,
  alt,
  hoverEffect = false,
  isTaken = false,
  useThumbnail = false,
  loading = "lazy",
  fallbackComponent,
  isReelThumbnail = false,
}) => {
  const { selectedClient } = useAppStore();
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [retryCount, setRetryCount] = useState(0);
  const [imageSrc, setImageSrc] = useState('');
  const [isCached, setIsCached] = useState(false);

  const getAuthenticatedUrl = useCallback(() => {
    if (useThumbnail) {
      return mediaItem.getThumbnailUrlWithQueryAuth();
    }
    return mediaItem.getDisplayUrl();
  }, [useThumbnail, mediaItem.fileIdentity, mediaItem.backendData.updatedAt]);

  const getCleanUrl = useCallback(() => {
    if (useThumbnail) {
      return mediaItem.getCleanThumbnailUrl();
    }
    return mediaItem.getCleanUrl();
  }, [useThumbnail, mediaItem.fileIdentity, mediaItem.backendData.updatedAt]);

  // Load image with IndexedDB caching
  useEffect(() => {
    let mounted = true;

    const loadImage = async () => {
      try {
        // Check processing status first for non-thumbnail usage
        // Allow image display during AI analysis (PROCESSING_AI status)
        if (!useThumbnail && !mediaItem.isReadyForDisplay() && mediaItem.backendData.status !== MediaStatus.PROCESSING_AI) {
          if (mounted) {
            if (mediaItem.processingStatus === "processing") {
              setLoadingState('downloading');
            } else {
              setLoadingState('pending_download');
            }
            setImageSrc('');
            setIsCached(false);
          }
          return;
        }

        setLoadingState('loading');

        // Use file identity for caching (storage_id or id)
        const fileId = useThumbnail
          ? `${mediaItem.fileIdentity}_thumb`
          : mediaItem.fileIdentity;

        if (!selectedClient?.id) {
          // No client context, use direct URL
          if (mounted) {
            setImageSrc(getAuthenticatedUrl());
            setIsCached(false);
          }
          return;
        }

        // Try to get from cache first
        const cachedUrl = await fileCacheService.getCachedFile(fileId);

        if (cachedUrl && mounted) {
          setImageSrc(cachedUrl);
          setIsCached(true);
          setLoadingState('cached');
          setRetryCount(0);
          return;
        }

        // Not cached - use authenticated URL and cache it
        if (mounted) {
          const url = getCleanUrl(); // Get clean URL for caching

          // Start loading with authenticated URL
          setImageSrc(getAuthenticatedUrl());
          setIsCached(false);
          setLoadingState('loading');
          setRetryCount(0);

          // Skip caching for video thumbnails (they don't support format=json)
          const isVideoThumbnail = mediaItem.isVideo() && useThumbnail;

          if (!isVideoThumbnail) {
            // Cache in background (don't await - let it happen async)
            fileCacheService.cacheFile(url, fileId, selectedClient.id)
              .then((blobUrl) => {
                if (mounted && blobUrl !== url) {
                  // Update to blob URL once cached
                  setImageSrc(blobUrl);
                  setIsCached(true);
                }
              })
              .catch(() => {
                // Keep using authenticated URL
              });
          }
        }
      } catch (error) {
        if (mounted) {
          // Fallback to authenticated URL
          setImageSrc(getAuthenticatedUrl());
          setIsCached(false);
          setLoadingState('loading');
        }
      }
    };

    loadImage();

    return () => {
      mounted = false;
    };
  }, [
    mediaItem.fileIdentity,
    mediaItem.processingStatus,
    mediaItem.backendData.updatedAt,
    useThumbnail,
    selectedClient?.id,
    getAuthenticatedUrl,
    getCleanUrl
  ]);

  // CSS classes for different aspect ratios
  const getAspectRatioClass = () => {
    switch (aspectRatio) {
      case "4:5":
        return "aspect-4/5";
      case "1:1":
        return "aspect-square";
      case "16:9":
        return "aspect-video";
      case "9:16":
        return "aspect-9/16";
      default:
        return "";
    }
  };

  const handleImageError = useCallback((e: any) => {
    if (retryCount < 2) {
      // Backend is likely downloading on-demand
      setLoadingState(useThumbnail ? 'generating' : 'downloading');
      setRetryCount(prev => prev + 1);

      // Retry after delay to allow backend processing
      setTimeout(() => {
        setLoadingState('retrying');
        // Force reload by updating src with cache-busting parameter
        const baseUrl = getAuthenticatedUrl();
        const separator = baseUrl.includes('?') ? '&' : '?';
        const newUrl = `${baseUrl}${separator}retry=${retryCount + 1}&t=${Date.now()}`;
        setImageSrc(newUrl);
      }, useThumbnail ? 5000 : 3000); // Longer delay for video processing
    } else {
      setLoadingState('error');
    }
  }, [retryCount, mediaItem, useThumbnail, getAuthenticatedUrl, imageSrc]);

  const handleImageLoad = useCallback(() => {
    setLoadingState(isCached ? 'cached' : 'loaded');
  }, [isCached]);

  const getLoadingMessage = () => {
    switch (loadingState) {
      case 'pending_download':
        return 'Queued for download';
      case 'downloading':
        return useThumbnail ? 'Generating thumbnail...' : 'Downloading...';
      case 'generating':
        return 'Generating thumbnail...';
      case 'retrying':
        return `Retrying... (${retryCount}/2)`;
      case 'error':
        return 'Failed to load';
      default:
        return 'Loading...';
    }
  };

  // Error state
  if (loadingState === 'error') {
    if (fallbackComponent) {
      return <>{fallbackComponent}</>;
    }

    return (
      <div className={`relative ${className}`} onClick={onClick}>
        <div className={`${getAspectRatioClass()} w-full h-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-sm rounded-lg`}>
          <div className="text-center">
            <svg className="w-8 h-8 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                clipRule="evenodd"
              />
            </svg>
            <p>Failed to load</p>
            <p className="text-xs mt-1">After {retryCount} attempts</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} onClick={onClick}>
      <div className={`${getAspectRatioClass()} w-full h-full relative overflow-hidden rounded-lg bg-gray-100 group`}>
        {/* Main image - only render if we have a valid src */}
        {imageSrc && (
          <img
            src={imageSrc}
            alt={alt || mediaItem.originalFilename}
            className={`
              w-full h-full object-cover object-center
              transition-all duration-300
              ${onClick ? "cursor-pointer" : ""}
              ${hoverEffect && !isTaken ? "group-hover:scale-105" : ""}
              ${loadingState === 'loaded' || loadingState === 'cached' ? 'opacity-100' : 'opacity-0'}
            `}
            onError={handleImageError}
            onLoad={handleImageLoad}
            loading={loading}
          />
        )}

        {/* Loading states - only show if not processing (to avoid double loaders) */}
        {!mediaItem.isProcessing() && !mediaItem.isProcessingFailed() && (!imageSrc || (loadingState === 'loading' || loadingState === 'downloading' || loadingState === 'retrying' || loadingState === 'pending_download' || loadingState === 'generating')) && (
          <div className="absolute inset-0 bg-linear-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <div className="text-center">
              {!useThumbnail && !mediaItem.isReadyForDisplay() && mediaItem.backendData.status !== MediaStatus.PROCESSING_AI ? (
                // File not ready - show processing status (but not during AI analysis)
                mediaItem.processingStatus === "processing" ? (
                  <>
                    <div className="w-6 h-6 text-blue-500 mx-auto mb-2">
                      <div className="w-full h-full border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <p className="text-xs text-blue-600">Processing...</p>
                  </>
                ) : (
                  <>
                    <div className="w-6 h-6 text-gray-400 mx-auto mb-2">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-xs text-gray-500">Pending</p>
                  </>
                )
              ) : (
                // File ready or thumbnail - standard loading
                <>
                  <div className="w-6 h-6 text-gray-300 mx-auto mb-2">
                    {useThumbnail ? (
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    ) : (
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">{getLoadingMessage()}</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Processing overlay - minimal loader */}
        {mediaItem.isProcessing() && (
          <div className="absolute inset-0 bg-linear-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {mediaItem.isProcessingFailed() && (
          <div className="absolute inset-0 bg-red-500 bg-opacity-20 flex items-center justify-center">
            <div className="bg-red-50 border border-red-200 rounded-lg p-2">
              <span className="text-red-700 text-xs font-medium">⚠️ Processing failed</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Memoize to prevent re-renders when mediaItem reference changes but identity stays same
export const UnifiedAuthenticatedImage = React.memo(UnifiedAuthenticatedImageComponent, (prevProps, nextProps) => {
  // Only re-render if meaningful props change
  return (
    prevProps.mediaItem.fileIdentity === nextProps.mediaItem.fileIdentity &&
    prevProps.mediaItem.processingStatus === nextProps.mediaItem.processingStatus &&
    prevProps.mediaItem.backendData.updatedAt === nextProps.mediaItem.backendData.updatedAt &&
    prevProps.useThumbnail === nextProps.useThumbnail &&
    prevProps.aspectRatio === nextProps.aspectRatio &&
    prevProps.className === nextProps.className
  );
});